import { cacheService } from './cache.service.js'
import { apiService } from './api.service.js'
import { categoriaCacheService } from './categoria-cache.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de cache para orçamentos com cache-first e respostas enriquecidas
 * Implementa padrão cache-first com TTL de 10 minutos,
 * deduplicação de requisições simultâneas por período,
 * processamento de respostas enriquecidas do backend
 * (derivados.orcamentosRestantes) e fallback offline.
 *
 * Chave de cache: orcamentos_MM_AAAA (por período)
 * Eventos disparados: orcamentos-cache-updated
 */
export class OrcamentoCacheService {
  constructor() {
    this.cacheKeyPrefix = 'orcamentos_'
    this.endpoint = 'orcamentos'
    /** Map de Promises em andamento para deduplicação por período */
    this.loadingPromises = new Map()
    /** Set de IDs de categorias conhecidas para detectar criação vs edição */
    this._categoriasConhecidas = new Set()
    /** Flag para suprimir criação automática quando o modal de orçamento está aberto */
    this.suprimirCriacaoAutomatica = false

    // Listener para criação automática de orçamento ao criar categoria
    window.addEventListener('categorias-cache-updated', () => {
      this._handleCategoriaCreated()
    })
  }

  /**
   * Lista orçamentos do período atual (mês corrente)
   * Delega para listarPorPeriodo com mês/ano atuais
   *
   * @returns {Promise<Array>} Lista de orçamentos do mês corrente
   */
  async listar() {
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())

    return this.listarPorPeriodo(mes, ano)
  }

  /**
   * Busca orçamentos por filtro de período (formato YYYY-MM)
   * Se filtro não especificado ou 'todos', retorna mês corrente
   *
   * @param {Object} filtros - Filtros de busca
   * @param {string} [filtros.periodo] - Período no formato YYYY-MM
   * @returns {Promise<Array>} Lista de orçamentos filtrados
   */
  async buscarPorFiltro(filtros = {}) {
    if (filtros.periodo && filtros.periodo !== 'todos') {
      const [ano, mes] = filtros.periodo.split('-')
      return this.listarPorPeriodo(mes, ano)
    }
    return this.listar()
  }

  /**
   * Lista orçamentos de um período específico com cache-first (TTL de 10 minutos)
   * Usa cacheService.isValid() para verificação centralizada de TTL
   * Implementa deduplicação via loadingPromises Map (por período)
   * Implementa fallback offline retornando cache expirado quando sem conexão
   * Filtra registros inativos (ativo === false) antes de retornar,
   * pois o cache local pode conter registros desabilitados por soft-delete
   *
   * @param {string} mes - Mês no formato MM (ex: '01')
   * @param {string} ano - Ano no formato AAAA (ex: '2025')
   * @returns {Promise<Array>} Lista de orçamentos ativos do período
   */
  async listarPorPeriodo(mes, ano) {
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
    const periodoKey = `${mes}_${ano}`

    // Cache-first: verifica validade com TTL padrão de 10 minutos
    if (cacheService.isValid(cacheKey)) {
      const cached = cacheService.get(cacheKey) || []
      return cached.filter(item => item.ativo !== false)
    }

    // Fallback offline: retorna cache expirado quando sem conexão
    if (!navigator.onLine) {
      const cacheExpirado = cacheService.get(cacheKey)
      const dados = cacheExpirado || []
      return dados.filter(item => item.ativo !== false)
    }

    // Deduplicação: reutiliza Promise em andamento para o mesmo período
    if (this.loadingPromises.has(periodoKey)) {
      return this.loadingPromises.get(periodoKey)
    }

    // Cache miss: busca do backend
    const promise = this._loadPeriodoFromBackend(mes, ano, cacheKey)
    this.loadingPromises.set(periodoKey, promise)

    try {
      const result = await promise
      return result
    } finally {
      this.loadingPromises.delete(periodoKey)
    }
  }

  /**
   * Carrega orçamentos de um período do backend via GET.
   * Backend retorna: { success: true, data: [...] } (retrocompatibilidade).
   * Em caso de timeout de autenticação (AUTH_TIMEOUT), retorna dados do cache local
   * ou array vazio — sem propagar exceção e sem executar requisição à API.
   *
   * @param {string} mes - Mês no formato MM
   * @param {string} ano - Ano no formato AAAA
   * @param {string} cacheKey - Chave de cache para armazenamento
   * @returns {Promise<Array>} Lista de orçamentos do período
   * @private
   */
  async _loadPeriodoFromBackend (mes, ano, cacheKey) {
    try {
      const response = await apiService.get(`${this.endpoint}?mes=${mes}&ano=${ano}`)
      const orcamentos = response.data?.data || response.data || response || []

      // Enriquece orçamentos sem categoria com dados do cache de categorias
      // Necessário quando o backend não consegue enriquecer (categoria recém-criada)
      const categorias = cacheService.get(categoriaCacheService.cacheKey) || []
      for (const orc of orcamentos) {
        if (!orc.categoria && orc.categoriaId) {
          const cat = categorias.find(c => c.id === orc.categoriaId)
          if (cat) {
            orc.categoria = cat
          }
        }
      }

      cacheService.set(cacheKey, orcamentos)
      return orcamentos
    } catch (error) {
      // Em timeout de auth, retorna cache local ou array vazio (Req 5.2)
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        const cacheLocal = cacheService.get(cacheKey)
        return cacheLocal || []
      }
      throw error
    }
  }

  /**
   * Cria novo orçamento no backend e atualiza o cache local
   * Antes de criar, verifica no cache local se existe um orçamento inativo
   * (ativo === false) para a mesma categoria+período. Se existir, reativa
   * o registro existente via PUT com ativo=true e novo valorOrcado.
   * Se não existir, cria novo registro via POST normalmente.
   * Processa resposta enriquecida: { success: true, data: { entidade, derivados, avisos } }
   * A entidade retornada inclui valorGasto e percentual calculados dinamicamente
   * Dispara evento orcamentos-cache-updated
   *
   * @param {Object} dados - Dados do orçamento a ser criado
   * @returns {Promise<Object>} Orçamento criado ou reativado pelo backend (com valorGasto e percentual)
   */
  async criar(dados) {
    // Verifica se existe registro inativo no cache local para a mesma categoria+período
    const registroInativo = this._buscarRegistroInativoNoCache(dados.categoriaId, dados.periodo || dados.mesAno)

    if (registroInativo) {
      // Reativa o registro existente via PUT (ativo=true + novo valorOrcado)
      return this._reativarOrcamento(registroInativo.id, dados)
    }

    // Verifica se existe registro ativo com valorLimite=0 (criado automaticamente)
    // Se existir, atualiza em vez de criar novo (evita 409 conflict)
    const registroAutomatico = this._buscarRegistroAutomaticoNoCache(dados.categoriaId, dados.periodo || dados.mesAno)
    if (registroAutomatico) {
      return this.atualizar(registroAutomatico.id, dados)
    }

    // Criação normal via POST
    const response = await apiService.post(this.endpoint, dados)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Enriquece entidade com dados da categoria do cache (nome, emoji)
    // O backend pode não retornar o objeto categoria completo na criação
    if (!entidade.categoria && dados.categoriaId) {
      const categorias = cacheService.get(categoriaCacheService.cacheKey) || []
      const cat = categorias.find(c => c.id === dados.categoriaId)
      if (cat) {
        entidade.categoria = cat
      }
    }

    // Processa derivados
    this._processarDerivados(derivados)

    // Adiciona ao cache do período correspondente
    const periodo = entidade.periodo || entidade.mesAno
    if (periodo) {
      const { mes, ano } = this._extrairPeriodo(periodo)
      const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`

      if (cacheService.isValid(cacheKey)) {
        cacheService.addToArray(cacheKey, entidade)
      }
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))

    return entidade
  }

  /**
   * Atualiza orçamento existente no backend e sincroniza o cache local
   * Processa resposta enriquecida e dispara evento orcamentos-cache-updated
   * A entidade retornada inclui valorGasto recalculado e percentual atualizado
   *
   * @param {string} id - ID do orçamento a ser atualizado
   * @param {Object} dados - Dados atualizados do orçamento
   * @returns {Promise<Object>} Orçamento atualizado pelo backend
   */
  async atualizar(id, dados) {
    const response = await apiService.put(`${this.endpoint}/${id}`, dados)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Processa derivados
    this._processarDerivados(derivados)

    // Mescla entidade retornada com dados existentes no cache
    // Preserva campos enriquecidos (categoria, etc.) que o backend pode não retornar
    const entidadeEnriquecida = this._mesclarComCacheExistente(id, entidade)

    // Atualiza no cache de todos os períodos
    this._updateInAllCaches(id, entidadeEnriquecida)

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))

    return entidadeEnriquecida
  }

  /**
   * Realiza soft-delete de um orçamento (exclusão lógica)
   * Envia PUT com { ativo: false } ao backend em vez de DELETE físico,
   * marca o registro como inativo no cache local (ativo=false) para
   * permitir reativação futura, e dispara evento de atualização.
   * Em caso de erro de rede/timeout, retorna o erro sem alterar o estado do cache.
   *
   * @param {string} id - ID do orçamento a ser desativado
   * @returns {Promise<Object>} Resposta do backend com o registro atualizado
   * @throws {Error} Propaga erro de rede/timeout sem alterar estado
   */
  async softDelete(id) {
    const response = await apiService.put(`${this.endpoint}/${id}`, { ativo: false })

    // Marca como inativo no cache local (mantém registro para possível reativação)
    this._markAsInactiveInAllCaches(id)

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))

    return response
  }

  /**
   * Exclui orçamento do backend e remove do cache local
   * Processa resposta enriquecida: derivados.orcamentosRestantes substitui
   * integralmente o cache do período (lista completa de orçamentos ativos restantes)
   * Dispara evento orcamentos-cache-updated
   *
   * @param {string} id - ID do orçamento a ser excluído
   * @param {Object} [opcoes] - Opções adicionais
   * @param {string} [opcoes.periodo] - Período do orçamento (formato YYYY-MM ou MM/AAAA)
   * @returns {Promise<boolean>} True se excluído com sucesso
   */
  async excluir(id, opcoes = {}) {
    const response = await apiService.delete(`${this.endpoint}/${id}`)
    const responseData = response.data || response

    // Extrai derivados da resposta enriquecida
    const derivados = responseData.derivados || {}

    // Processa derivados (orcamentosRestantes substitui integralmente o cache)
    this._processarDerivados(derivados, opcoes.periodo)

    // Se derivados.orcamentosRestantes não veio, remove do cache manualmente
    if (!derivados.orcamentosRestantes) {
      this._removeFromAllCaches(id)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))

    return true
  }

  /**
   * Processa entidades derivadas da resposta enriquecida do backend
   * - derivados.orcamentosRestantes: substitui integralmente o cache do período
   *   (lista completa de orçamentos ativos restantes após exclusão)
   *
   * @param {Object} derivados - Objeto com entidades derivadas do backend
   * @param {Array} [derivados.orcamentosRestantes] - Lista completa de orçamentos restantes
   * @param {string} [periodoContexto] - Período de contexto para substituição integral
   * @private
   */
  _processarDerivados(derivados, periodoContexto) {
    if (!derivados || Object.keys(derivados).length === 0) return

    // Substitui integralmente o cache do período com a lista de orçamentos restantes
    if (derivados.orcamentosRestantes) {
      // Tenta determinar o período a partir dos dados ou do contexto
      let mes, ano
      if (periodoContexto) {
        const extraido = this._extrairPeriodo(periodoContexto)
        mes = extraido.mes
        ano = extraido.ano
      } else if (derivados.orcamentosRestantes.length > 0) {
        // Extrai período do primeiro orçamento restante
        const primeiro = derivados.orcamentosRestantes[0]
        const extraido = this._extrairPeriodo(primeiro.periodo || primeiro.mesAno)
        mes = extraido.mes
        ano = extraido.ano
      } else {
        // Sem dados para determinar período, usa mês corrente
        const hoje = new Date()
        mes = String(hoje.getMonth() + 1).padStart(2, '0')
        ano = String(hoje.getFullYear())
      }

      const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
      cacheService.set(cacheKey, derivados.orcamentosRestantes)
    }
  }

  /**
   * Extrai mês e ano de um período nos formatos suportados
   * Formatos aceitos: 'YYYY-MM', 'MM/AAAA', 'MM_AAAA'
   *
   * @param {string} periodo - Período em formato string
   * @returns {{ mes: string, ano: string }} Mês (MM) e ano (AAAA)
   * @private
   */
  _extrairPeriodo(periodo) {
    if (!periodo) {
      const hoje = new Date()
      return {
        mes: String(hoje.getMonth() + 1).padStart(2, '0'),
        ano: String(hoje.getFullYear())
      }
    }

    if (periodo.includes('-')) {
      // Formato YYYY-MM
      const [ano, mes] = periodo.split('-')
      return { mes, ano }
    } else if (periodo.includes('/')) {
      // Formato MM/AAAA
      const [mes, ano] = periodo.split('/')
      return { mes, ano }
    } else if (periodo.includes('_')) {
      // Formato MM_AAAA
      const [mes, ano] = periodo.split('_')
      return { mes, ano }
    }

    // Fallback: mês corrente
    const hoje = new Date()
    return {
      mes: String(hoje.getMonth() + 1).padStart(2, '0'),
      ano: String(hoje.getFullYear())
    }
  }

  /**
   * Mescla a entidade retornada pelo backend com os dados existentes no cache.
   * Preserva campos enriquecidos (como `categoria` com nome/emoji) que o backend
   * pode não retornar em operações de atualização (PUT retorna apenas campos modificados).
   * Os dados do backend têm prioridade sobre os do cache para campos presentes em ambos.
   *
   * @param {string} id - ID do orçamento
   * @param {Object} entidadeBackend - Dados retornados pelo backend
   * @returns {Object} Entidade mesclada com dados enriquecidos preservados
   * @private
   */
  _mesclarComCacheExistente(id, entidadeBackend) {
    const existenteNoCache = this.buscarPorId(id)
    if (!existenteNoCache) return entidadeBackend

    // Mescla: cache existente como base + dados do backend como override
    return { ...existenteNoCache, ...entidadeBackend }
  }

  /**
   * Atualiza orçamento em todos os caches de período
   * Percorre todas as chaves de cache de orçamentos do usuário
   *
   * @param {string} id - ID do orçamento
   * @param {Object} orcamento - Orçamento atualizado
   * @private
   */
  _updateInAllCaches(id, orcamento) {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => {
        const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
        cacheService.updateInArray(cacheKey, id, orcamento)
      })
  }

  /**
   * Remove orçamento de todos os caches de período
   * Percorre todas as chaves de cache de orçamentos do usuário
   *
   * @param {string} id - ID do orçamento a remover
   * @private
   */
  _removeFromAllCaches(id) {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => {
        const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
        cacheService.removeFromArray(cacheKey, id)
      })
  }

  /**
   * Marca orçamento como inativo (ativo=false) em todos os caches de período
   * Mantém o registro no cache para permitir verificação de reativação futura
   * Percorre todas as chaves de cache de orçamentos do usuário
   *
   * @param {string} id - ID do orçamento a marcar como inativo
   * @private
   */
  _markAsInactiveInAllCaches(id) {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => {
        const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
        const cached = cacheService.get(cacheKey) || []
        const index = cached.findIndex(item => item && item.id === id)
        if (index >= 0) {
          cached[index] = { ...cached[index], ativo: false }
          cacheService.set(cacheKey, cached)
        }
      })
  }

  /**
   * Busca registro ativo com valorLimite=0 no cache local para uma categoria e período.
   * Identifica orçamentos criados automaticamente (ao criar categoria) que ainda não
   * tiveram valor definido pelo usuário. Usado pelo método criar() para atualizar
   * o registro existente em vez de criar duplicata (evita 409 conflict).
   *
   * @param {string} categoriaId - ID da categoria a verificar
   * @param {string} periodo - Período no formato suportado (YYYY-MM, MM/AAAA, MM_AAAA)
   * @returns {Object|null} Registro automático encontrado ou null
   * @private
   */
  _buscarRegistroAutomaticoNoCache(categoriaId, periodo) {
    if (!cacheService.currentUserUid || !categoriaId || !periodo) return null

    const { mes, ano } = this._extrairPeriodo(periodo)
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
    const cached = cacheService.get(cacheKey) || []

    return cached.find(item =>
      item &&
      item.categoriaId === categoriaId &&
      item.ativo !== false &&
      (item.valorLimite === 0 || item.valorLimite === undefined) &&
      (item.valorOrcado === 0 || item.valorOrcado === undefined)
    ) || null
  }

  /**
   * Busca registro inativo no cache local para uma categoria e período específicos
   * Verifica se existe orçamento com ativo === false para a mesma categoriaId e período.
   * Usado pelo método criar() para decidir entre reativação e criação.
   *
   * @param {string} categoriaId - ID da categoria a verificar
   * @param {string} periodo - Período no formato suportado (YYYY-MM, MM/AAAA, MM_AAAA)
   * @returns {Object|null} Registro inativo encontrado ou null
   * @private
   */
  _buscarRegistroInativoNoCache(categoriaId, periodo) {
    if (!cacheService.currentUserUid || !categoriaId || !periodo) return null

    const { mes, ano } = this._extrairPeriodo(periodo)
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
    const cached = cacheService.get(cacheKey) || []

    return cached.find(item =>
      item &&
      item.categoriaId === categoriaId &&
      item.ativo === false
    ) || null
  }

  /**
   * Reativa um orçamento existente que estava desabilitado (ativo=false)
   * Envia PUT ao backend com ativo=true e novo valorOrcado,
   * atualiza o registro no cache local e dispara evento de atualização.
   *
   * @param {string} id - ID do orçamento a ser reativado
   * @param {Object} dados - Dados contendo o novo valorOrcado (e outros campos opcionais)
   * @returns {Promise<Object>} Orçamento reativado pelo backend
   * @private
   */
  async _reativarOrcamento(id, dados) {
    const dadosReativacao = {
      ativo: true,
      valorLimite: dados.valorLimite || dados.valorOrcado || 0,
      valorOrcado: dados.valorOrcado || dados.valorLimite || 0
    }

    const response = await apiService.put(`${this.endpoint}/${id}`, dadosReativacao)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData

    // Mescla com dados existentes no cache para preservar campos enriquecidos (categoria)
    const entidadeEnriquecida = this._mesclarComCacheExistente(id, entidade)

    // Atualiza no cache local (substitui o registro inativo pelo reativado)
    this._updateInAllCaches(id, entidadeEnriquecida)

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))

    return entidadeEnriquecida
  }

  /**
   * Busca orçamento por ID no cache local (todos os períodos)
   *
   * @param {string} id - ID do orçamento
   * @returns {Object|null} Orçamento encontrado ou null
   */
  buscarPorId(id) {
    if (!cacheService.currentUserUid) return null

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(userPrefix)) {
        const cacheKey = key.replace(`equilibra_cache_${cacheService.currentUserUid}_`, '')
        const orcamentos = cacheService.get(cacheKey) || []
        const encontrado = orcamentos.find(orc => orc.id === id)
        if (encontrado) return encontrado
      }
    }
    return null
  }

  /**
   * Força recarregamento do cache de orçamentos do mês corrente
   * Remove cache existente e busca dados atualizados do backend
   *
   * @returns {Promise<Array>} Lista atualizada de orçamentos
   */
  async recarregar() {
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())
    const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`

    cacheService.remove(cacheKey)
    return this.listarPorPeriodo(mes, ano)
  }

  /**
   * Inicializa cache no startup do sistema
   * Só busca do backend se não tem cache válido para o mês corrente
   * Também inicializa o Set de categorias conhecidas para detecção de novas categorias
   *
   * @returns {Promise<void>}
   */
  async inicializarCache() {
    try {
      const hoje = new Date()
      const mes = String(hoje.getMonth() + 1).padStart(2, '0')
      const ano = String(hoje.getFullYear())
      const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`

      if (!cacheService.isValid(cacheKey)) {
        await this._loadPeriodoFromBackend(mes, ano, cacheKey)
      }

      // Inicializa Set de categorias conhecidas para detectar futuras criações
      this._atualizarCategoriasConhecidas()
    } catch (error) {
      console.error('❌ Erro ao inicializar cache de orçamentos:', error)
    }
  }

  /**
   * Limpa todos os caches de orçamentos do usuário atual
   */
  limparCache() {
    if (!cacheService.currentUserUid) return

    const userPrefix = `equilibra_cache_${cacheService.currentUserUid}_${this.cacheKeyPrefix}`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => localStorage.removeItem(key))
  }

  /**
   * Atualiza o Set de categorias conhecidas a partir do cache atual
   * Utilizado para detectar novas categorias na próxima vez que
   * o evento `categorias-cache-updated` for disparado.
   *
   * @returns {Array} Lista de categorias do cache
   * @private
   */
  _atualizarCategoriasConhecidas() {
    const categorias = cacheService.get(categoriaCacheService.cacheKey) || []
    this._categoriasConhecidas = new Set(categorias.map(c => c.id))
    return categorias
  }

  /**
   * Handler para o evento `categorias-cache-updated`
   * Detecta se houve criação de nova categoria (não edição) comparando
   * o Set de categorias conhecidas com o cache atualizado.
   * Se identificar novas categorias, cria orçamento automático com valorOrcado=0
   * para o período corrente, sem reativar orçamentos desabilitados (Req 4.4).
   *
   * Em caso de erro, registra em console sem bloquear a criação da categoria (Req 7.5).
   *
   * @private
   */
  async _handleCategoriaCreated() {
    try {
      // Se o modal de orçamento está aberto em modo criação, suprime a criação automática
      // O usuário vai criar o orçamento manualmente com o valor desejado
      if (this.suprimirCriacaoAutomatica) {
        // Apenas atualiza o Set de conhecidas para manter sincronizado
        const categoriasAtuais = cacheService.get(categoriaCacheService.cacheKey) || []
        this._categoriasConhecidas = new Set(categoriasAtuais.map(c => c.id))
        return
      }

      // Obtém categorias atuais do cache
      const categoriasAtuais = cacheService.get(categoriaCacheService.cacheKey) || []

      // Identifica categorias novas (criação, não edição)
      // Se o Set de conhecidas está vazio e há categorias, é a primeira carga — preenche sem criar orçamentos
      if (this._categoriasConhecidas.size === 0 && categoriasAtuais.length > 0) {
        this._categoriasConhecidas = new Set(categoriasAtuais.map(c => c.id))
        return
      }

      // Detecta IDs que não existiam antes (novas categorias)
      const novasCategorias = categoriasAtuais.filter(
        cat => !this._categoriasConhecidas.has(cat.id)
      )

      // Atualiza Set de conhecidas para refletir estado atual
      this._categoriasConhecidas = new Set(categoriasAtuais.map(c => c.id))

      // Se não há novas categorias, foi edição ou exclusão — ignora
      if (novasCategorias.length === 0) return

      // Obtém período corrente
      const hoje = new Date()
      const mes = String(hoje.getMonth() + 1).padStart(2, '0')
      const ano = String(hoje.getFullYear())

      // Para cada nova categoria, cria orçamento automático
      for (const novaCategoria of novasCategorias) {
        await this._criarOrcamentoAutomatico(novaCategoria.id, mes, ano)
      }
    } catch (error) {
      console.error('Erro na criação automática de orçamento:', error)
      // Não impede a criação da categoria (Req 7.5)
    }
  }

  /**
   * Cria orçamento automático com valorOrcado=0 para uma nova categoria no período corrente.
   * Verifica se já existe orçamento ativo para a categoria no período.
   * Se existir orçamento desabilitado (ativo=false), mantém inalterado e cria novo registro
   * independente (Req 4.4 — processos automáticos não reativam desabilitados).
   *
   * @param {string} categoriaId - ID da nova categoria
   * @param {string} mes - Mês corrente no formato MM
   * @param {string} ano - Ano corrente no formato AAAA
   * @private
   */
  async _criarOrcamentoAutomatico(categoriaId, mes, ano) {
    try {
      // Busca orçamentos do período no cache (incluindo inativos, caso o backend retorne)
      const cacheKey = `${this.cacheKeyPrefix}${mes}_${ano}`
      const orcamentosPeriodo = cacheService.get(cacheKey) || []

      // Verifica se já existe orçamento ATIVO para a categoria no período
      const orcamentoAtivoExistente = orcamentosPeriodo.find(
        orc => orc.categoriaId === categoriaId && orc.ativo !== false
      )

      // Se já existe orçamento ativo, não cria duplicata
      if (orcamentoAtivoExistente) return

      // Cria novo orçamento com valorOrcado=0 via API
      // Nota: não usa this.criar() para evitar lógica de reativação (Req 4.4)
      // Processos automáticos NUNCA reativam orçamentos desabilitados
      const periodo = `${ano}-${mes}`
      const response = await apiService.post(this.endpoint, {
        categoriaId,
        valorOrcado: 0,
        periodo,
        ativo: true
      })

      const responseData = response.data || response
      const entidade = responseData.entidade || responseData

      // Enriquece entidade com dados da categoria do cache (nome, emoji)
      // O backend pode não retornar o objeto categoria completo na criação
      if (!entidade.categoria && categoriaId) {
        const categorias = cacheService.get(categoriaCacheService.cacheKey) || []
        const cat = categorias.find(c => c.id === categoriaId)
        if (cat) {
          entidade.categoria = cat
        }
      }

      // Adiciona ao cache local
      if (cacheService.isValid(cacheKey)) {
        cacheService.addToArray(cacheKey, entidade)
      }

      // Dispara evento de atualização para UI
      window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))
    } catch (error) {
      console.error(`Erro ao criar orçamento automático para categoria ${categoriaId}:`, error)
      // Não propaga erro — criação da categoria não deve ser bloqueada (Req 7.5)
    }
  }
}

// Instância singleton
export const orcamentoCacheService = new OrcamentoCacheService()
